import type { AuditExport, AuditExportTicket } from "@closer/shared";
import { AUDIT_EXPORT_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * AUDIT DIŞA AKTARMA PANELİ (CLOS-194 `[172]` · alt görev CLOS-911).
 *
 * ### ⚠ Sunucu tarafı vardı, soran yüzey YOKTU
 *
 * `POST /api/audit/exports`, `GET /api/audit/exports`, indirme bileti, iptal ve yeniden
 * hazırlama uçlarının hepsi yazılıydı ve 42 testle korunuyordu; Audit ekranında tek bir
 * satır karşılığı yoktu. Yani hazırlanan dosya hiçbir kullanıcının eline geçmiyordu —
 * "üretiliyor, kapısı yazılı, soran yüzey yok" ailesi.
 *
 * ### ⚠ EXPORT AYRI BİR YETKİ, AYRI BİR AKSİYON
 *
 * Kartın kriteri: *"Export, Audit görüntüleme aksiyonundan ayrı ve yalnız export
 * permission'ı olan kullanıcıya gösterilen açık bir aksiyondur."* Panel
 * `organization.audit_export` yoksa HİÇ çizilmiyor — okuma yetkisiyle görünen bir düğme,
 * her tıklamada aynı 403'e çarpardı.
 *
 * ### ⚠ KAPSAM ÖNCEDEN YAZILI
 *
 * Kartın kriteri: *"Kullanıcı export başlatmadan önce aktif filtre/kapsamın dışa
 * aktarılacağını görür; tüm organizasyon verisi export ediliyormuş izlenimi verilmez."*
 * Kapsam cümlesi ekranın üstündeki süzgeçlerden türüyor ve isteğe giden değerlerle AYNI
 * kaynaktan geliyor; iki ayrı yerde hesaplasaydık gösterilen kapsam ile gönderilen kapsam
 * ayrışabilirdi.
 *
 * ### ⚠ HAM ADRES DEĞİL, KISA ÖMÜRLÜ BİLET
 *
 * İndirme iki adımlı: önce sunucudan bilet isteniyor (yetki o anda YENİDEN sorulyor),
 * sonra dönen imzalı adres açılıyor. Ham depolama adresini listede tutsaydık, bir kez
 * sızan bağlantı süresiz geçerli olurdu.
 *
 * ### ⚠ DURUM SORULARAK ÖĞRENİLİYOR
 *
 * Hazırlanma birkaç saniye sürüyor. Açık iş varken liste kısa aralıkla yenileniyor;
 * sessiz bir bekleme, arızadan ayırt edilemez. İş biter bitmez yenileme duruyor — sürekli
 * dönen bir sorgu, ekranı açık unutan kullanıcıda boşuna yük olurdu.
 */
export function AuditExportPanel({
  tenantId,
  filtreler,
  kapsamMetni,
}: {
  tenantId: string;
  /** İsteğe gidecek süzgeçler — ekranın listeyi çektiği değerlerin AYNISI. */
  filtreler: Record<string, string>;
  /** Kullanıcının okuduğu kapsam cümlesi. */
  kapsamMetni: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { preferences } = usePreferences();

  const anahtar = tenantKey(tenantId, "audit-exports");

  const sorgu = useQuery({
    queryKey: anahtar,
    queryFn: async () => (await api<AuditExport[]>("/api/audit/exports", { tenantId })).data,
    /**
     * ⚠ Yenileme YALNIZ açık iş varken. Sabit aralıkla dönseydik, hiçbir işi olmayan
     * kullanıcı da sonsuza kadar sunucuya sorardı.
     */
    refetchInterval: (q) =>
      (q.state.data ?? []).some((i) => i.status === "pending" || i.status === "processing")
        ? 3000
        : false,
  });

  const talep = useMutation({
    mutationFn: async () =>
      (
        await api<AuditExport>("/api/audit/exports", {
          method: "POST",
          tenantId,
          body: JSON.stringify(filtreler),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Dışa aktarma hazırlanıyor");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Dışa aktarma başlatılamadı"),
  });

  const indir = useMutation({
    mutationFn: async (id: string) =>
      (await api<AuditExportTicket>(`/api/audit/exports/${id}/download`, { tenantId })).data,
    /**
     * ⚠ YENİ SEKME DEĞİL, DOĞRUDAN İNDİRME.
     *
     * `window.open` ilk yazımdı ve kırılgandı: çağrı tıklamanın kendisinde değil, bilet
     * isteği döndükten SONRAKİ eşzamansız devamda oluyor ve tarayıcılar kullanıcı
     * hareketinden kopmuş pencere açmalarını engelliyor. Kullanıcı düğmeye basar, hiçbir
     * şey olmaz, hata da görmez — sessiz başarısızlık.
     *
     * İmzalı adres `Content-Disposition: attachment` taşıyor (`lib/storage/presign.ts`),
     * yani adrese gitmek sayfadan çıkmıyor, dosyayı indiriyor. Engelleyiciye takılan bir
     * yol yerine tarayıcının indirme yolunu kullanıyoruz.
     */
    onSuccess: (bilet) => {
      window.location.assign(bilet.downloadUrl);
    },
    /**
     * ⚠ Süresi dolmuş ya da yetkisi kalmamış bir bilet SESSİZCE geçilmiyor. Kartın
     * kriteri: *"yarım dosya başarı göstermez."* Bağlantı açılmadığında kullanıcı neden
     * açılmadığını okumalı, yoksa tarayıcısının bozuk olduğunu düşünür.
     */
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Dosya şu an indirilemiyor"),
  });

  const iptal = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/audit/exports/${id}/cancel`, { method: "POST", tenantId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Dışa aktarma iptal edildi");
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "İptal edilemedi"),
  });

  const yeniden = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/audit/exports/${id}/retry`, { method: "POST", tenantId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Yeniden hazırlanıyor");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Yeniden hazırlanamadı"),
  });

  const isler = sorgu.data ?? [];

  return (
    <Card title="Dışa aktarma">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p data-testid="export-kapsam" className="text-sm text-fg-muted">
            {kapsamMetni}
          </p>
          <div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              data-testid="export-baslat"
              loading={talep.isPending}
              disabled={talep.isPending}
              onClick={() => talep.mutate()}
            >
              Dışa aktarma hazırla
            </Button>
          </div>
        </div>

        {sorgu.isPending ? (
          <div role="status" aria-label="Dışa aktarmalar yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : sorgu.error ? (
          /**
           * ⚠ ÖNCE HATA, SONRA BOŞLUK. `!sorgu.data` diye sorsaydık, sorgu düştüğünde de
           * `data` `undefined` olur ve ekran "hiç dışa aktarma yok" derdi — hazırlanmış
           * bir dosya, bir arıza yüzünden yok gibi görünürdü.
           */
          <StateView
            state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
            onRetry={() => void sorgu.refetch()}
          />
        ) : isler.length === 0 ? (
          <p data-testid="export-yok" className="text-sm text-fg-muted">
            Henüz bir dışa aktarma hazırlamadınız.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {isler.map((is) => (
              <li
                key={is.id}
                data-testid="export-satiri"
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default pb-2 last:border-0 last:pb-0"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-2">
                    {/*
                      ⚠ DURUM METİNLE de yazılı, yalnız rozet rengiyle değil: renk tek
                      başına bilgi taşıyıcısı olamaz.
                    */}
                    <Badge tone={rozetTonu(is.status)}>
                      {AUDIT_EXPORT_STATUS_NAMES[is.status]}
                    </Badge>
                    <span className="text-sm text-fg">
                      {formatDateTime(is.createdAt, preferences.locale)}
                    </span>
                  </span>
                  {/*
                    ⚠ BAŞARISIZLIK SEBEBİ EKRANDA. Gizleseydik kullanıcı "hazırlanıyor"da
                    kalmış sanır ve süresiz beklerdi.
                  */}
                  {is.failureReason ? (
                    <span data-testid="export-hata-sebebi" className="text-xs text-danger">
                      {is.failureReason}
                    </span>
                  ) : null}
                  {is.status === "ready" && is.rowCount !== null ? (
                    <span className="text-xs text-fg-soft">
                      {is.rowCount} kayıt
                      {is.expiresAt
                        ? ` · ${formatDateTime(is.expiresAt, preferences.locale)} tarihine kadar indirilebilir`
                        : ""}
                    </span>
                  ) : null}
                </span>

                <span className="flex items-center gap-2">
                  {is.status === "ready" ? (
                    <Button
                      type="button"
                      size="sm"
                      data-testid="export-indir"
                      loading={indir.isPending}
                      onClick={() => indir.mutate(is.id)}
                    >
                      İndir
                    </Button>
                  ) : null}

                  {/*
                    ⚠ Süresi dolmuş ya da hazırlanamamış iş için YENİDEN HAZIRLA sunuluyor:
                    kullanıcı ölü bir satıra bakıp ne yapacağını bilemez kalmamalı. Hazır
                    ve açık işlerde YOK — sunucu ikisini de reddediyor.
                  */}
                  {is.status === "failed" || is.status === "expired" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      data-testid="export-yeniden"
                      loading={yeniden.isPending}
                      onClick={() => yeniden.mutate(is.id)}
                    >
                      Yeniden hazırla
                    </Button>
                  ) : null}

                  {/*
                    ⚠ İPTAL YALNIZ `pending` DURUMUNDA. `processing`'i de sunmak çekiciydi
                    ama sunucu onu 409 ile reddediyor ve gerekçesi sağlam: `prepareExport`
                    satırı kendi transaction'ında kilitliyor, iptal isteği o kilidi
                    beklerken iş çoktan dosyayı yazmış oluyor. Düğmeyi çizseydik kullanıcı
                    her hazırlanan işte "geç kaldınız" duvarına çarpardı.
                  */}
                  {is.status === "pending" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="export-iptal"
                      loading={iptal.isPending}
                      onClick={() => iptal.mutate(is.id)}
                    >
                      İptal et
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** Rozet tonu durumun ANLAMINDAN türüyor; renk metnin yerini almıyor, ona eşlik ediyor. */
function rozetTonu(status: AuditExport["status"]): "success" | "warning" | "danger" | "neutral" {
  if (status === "ready") return "success";
  if (status === "pending" || status === "processing") return "warning";
  if (status === "failed" || status === "expired") return "danger";
  return "neutral";
}
